<template>
  <div
    class="game-container"
    :class="{ 'has-expanded': expandedCardIndex !== -1 }"
  >
    <div
      class="cards-row"
      :class="{ 'has-expanded': expandedCardIndex !== -1 }"
    >
      <OddOneOutCard
        v-for="(card, index) in shuffledCards"
        :key="index"
        :text="card.text"
        :img="card.img"
        :showCorrect="card.showCorrect"
        :showIncorrect="card.showIncorrect"
        :isPronunciation="isPronunciation"
        :isDimmed="expandedCardIndex !== -1 && expandedCardIndex !== index"
        @click="handleCardClick(index)"
      />
    </div>
    <!-- Overlay card for explanation (not for pronunciation) -->
    <div
      v-if="expandedCardIndex !== -1 && !isPronunciation"
      class="overlay-card-container"
      @click="closeExpanded"
    >
      <div class="overlay-card" @click.stop>
        <!-- With image layout -->
        <template v-if="expandedCardData.img">
          <!-- Explanation text above image -->
          <div v-if="currentItem.reason" class="overlay-reason-top">
            {{ currentItem.reason }}
          </div>
          <!-- Image in middle -->
          <div
            class="overlay-image"
            :style="{
              backgroundImage: `url(${expandedCardData.img})`,
            }"
          ></div>
          <!-- Name at bottom -->
          <div class="overlay-text-bottom">{{ expandedCardData.text }}</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import OddOneOutCard from './OddOneOutCard.vue'
import consoleLog from '@/lib/consoleLog.js'

export default {
  name: 'GameContainer',
  components: {
    OddOneOutCard,
  },
  props: {
    content: Array,
  },
  data() {
    return {
      currentIndex: 0,
      shuffledCards: [],
      oddOneOutIndex: -1,
      expandedCardIndex: -1,
      gameStarted: false,
    }
  },
  computed: {
    currentItem() {
      return this.content[this.currentIndex]
    },
    isPronunciation() {
      return this.currentItem?.isPronunciation || false
    },
    expandedCardData() {
      if (this.expandedCardIndex === -1) return {}
      return this.shuffledCards[this.expandedCardIndex] || {}
    },
  },
  mounted() {
    this.setupRound()
  },
  methods: {
    setupRound() {
      if (this.currentIndex >= this.content.length) {
        this.$emit('game_finished')
        return
      }

      const item = this.currentItem

      // Combine regular cards and odd one out
      const allCards = [
        ...item.cards.map((card) => ({ ...card, isOdd: false })),
        { ...item.oddOneOut, isOdd: true },
      ]

      // Shuffle the cards
      this.shuffledCards = this.shuffleArray(allCards).map((card) => ({
        ...card,
        showCorrect: false,
        showIncorrect: false,
      }))

      // Find the index of the odd one out in the shuffled array
      this.oddOneOutIndex = this.shuffledCards.findIndex((card) => card.isOdd)

      this.expandedCardIndex = -1
      this.gameStarted = true

      consoleLog('Round setup:', {
        currentIndex: this.currentIndex,
        oddOneOutIndex: this.oddOneOutIndex,
        isPronunciation: this.isPronunciation,
      })

      this.$emit('round_started')
    },
    handleCardClick(index) {
      if (this.expandedCardIndex !== -1 && !this.isPronunciation) {
        // If a card is already expanded (and not pronunciation), clicking the card closes it and moves to next round
        this.closeExpanded()
        return
      }

      if (index === this.oddOneOutIndex) {
        // Correct answer
        this.shuffledCards[index].showCorrect = true
        if (this.isPronunciation) {
          // For pronunciation, just show tick and move to next round
          setTimeout(() => {
            this.nextRound()
          }, 1000)
        } else {
          // For other topics, show overlay card
          setTimeout(() => {
            this.expandedCardIndex = index
          }, 800)
        }
      } else {
        // Wrong answer
        this.shuffledCards[index].showIncorrect = true
        setTimeout(() => {
          this.shuffledCards[index].showIncorrect = false
        }, 1000)
      }
    },
    closeExpanded() {
      this.expandedCardIndex = -1
      setTimeout(() => {
        this.nextRound()
      }, 300)
    },
    nextRound() {
      // Reset correct/incorrect states
      this.shuffledCards.forEach((card) => {
        card.showCorrect = false
        card.showIncorrect = false
      })
      this.expandedCardIndex = -1
      this.currentIndex++
      if (this.currentIndex >= this.content.length) {
        this.$emit('game_finished')
      } else {
        this.setupRound()
      }
    },
    reset() {
      this.currentIndex = 0
      this.expandedCardIndex = -1
      this.setupRound()
    },
    shuffleArray(array) {
      const arrayCopy = [...array]
      for (let i = arrayCopy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[arrayCopy[i], arrayCopy[j]] = [arrayCopy[j], arrayCopy[i]]
      }
      return arrayCopy
    },
  },
  watch: {
    content() {
      this.reset()
    },
  },
}
</script>

<style scoped>
.game-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
}

.game-container.has-expanded {
  overflow: hidden;
}

.cards-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  width: 100%;
  max-width: 1200px;
  position: relative;
  z-index: 1;
  transition: opacity 0.8s ease;
}

.cards-row.has-expanded {
  z-index: 0;
}

.overlay-card-container {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s;
}

.overlay-card {
  width: 600px;
  max-width: 90vw;
  height: auto;
  min-height: 400px;
  max-height: 85vh;
  background-color: var(--secondary-green);
  border-radius: 8px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
  overflow: hidden;
  position: relative;
  animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: flex;
  flex-direction: column;
}

.overlay-reason-top {
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  font-size: 1.5rem;
  color: var(--almost-black);
  text-align: center;
  line-height: 1.4;
}

.overlay-image {
  width: 100%;
  height: 400px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  flex: 1;
}

.overlay-text-bottom {
  padding: 1rem 1.5rem 1.5rem 1.5rem;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--almost-black);
  text-align: center;
}

.overlay-text-only {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.overlay-text-only .overlay-reason-top {
  padding: 0;
}

.overlay-text-only .overlay-text {
  font-size: 2rem;
  color: var(--almost-black);
  font-weight: bold;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 1024px) {
  .cards-row {
    gap: 1rem;
  }
}

@media (max-width: 768px) {
  .game-container {
    padding: 1rem;
  }

  .cards-row {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .overlay-card {
    width: 90vw;
    min-height: 350px;
  }

  .overlay-image {
    height: 250px;
  }

  .overlay-reason-top {
    padding: 1rem 1rem 0.75rem 1rem;
    font-size: 1.25rem;
  }

  .overlay-text-bottom {
    padding: 0.75rem 1rem 1rem 1rem;
    font-size: 1.25rem;
  }

  .overlay-text-only .overlay-text {
    font-size: 1.5rem;
  }

  .overlay-text-only .overlay-reason-top {
    font-size: 1.25rem;
  }
}

@media (max-width: 480px) {
  .game-container {
    padding: 0.5rem;
  }

  .cards-row {
    gap: 0.5rem;
  }
}
</style>
